import React, { useEffect, useState } from 'react';
import Topbar from '../component/Topbar';
import LoadingScreen from '../component/LoadingScreen';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { acquireGetAccessToken } from '../utils/GetGrapApi';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { checkRole, UserRole } from '../callMsGroup';
import { saveRealRole, getEffectiveRole } from '../utils/roleOverride';
import { Buffer } from 'buffer';
import { useNavigate } from 'react-router-dom';
import { BiEnvelope, BiSend } from 'react-icons/bi';
import { useLang } from '../hooks/useLabels';

const LABELS = {
    page_title:          { en: 'Email Configuration',      vn: 'Cấu Hình Email' },
    section_smtp:        { en: 'SMTP Configuration',       vn: 'Cấu Hình SMTP' },
    section_test:        { en: 'Send Test Email',          vn: 'Gửi Email Thử Nghiệm' },
    smtp_host:           { en: 'SMTP Host',                vn: 'SMTP Host' },
    smtp_port:           { en: 'SMTP Port',                vn: 'Cổng SMTP' },
    from_email:          { en: 'From Email',               vn: 'Email Người Gửi' },
    from_name:           { en: 'From Name',                vn: 'Tên Người Gửi' },
    cooldown:            { en: 'Send Cooldown',            vn: 'Thời Gian Chờ Gửi' },
    hint_cooldown:       { en: 'Minimum wait between re-sends. Default: 5 min.', vn: 'Thời gian chờ tối thiểu giữa các lần gửi. Mặc định: 5 phút.' },
    app_base_url:        { en: 'App Base URL',             vn: 'URL Gốc Ứng Dụng' },
    hint_app_base_url:   { en: 'Frontend URL used to generate event deep links in notification emails.', vn: 'URL frontend dùng tạo liên kết sự kiện trong email thông báo.' },
    recipient_email:     { en: 'Recipient Email',          vn: 'Email Người Nhận' },
    btn_save:            { en: 'Save',                     vn: 'Lưu' },
    btn_saving:          { en: 'Saving...',                vn: 'Đang lưu...' },
    btn_reset:           { en: 'Reset',                    vn: 'Đặt lại' },
    btn_send_test:       { en: 'Send Test',                vn: 'Gửi Thử' },
    btn_sending:         { en: 'Sending...',               vn: 'Đang gửi...' },
    err_load:            { en: 'Failed to load email configuration',  vn: 'Không tải được cấu hình email' },
    err_save:            { en: 'Failed to save email configuration',  vn: 'Không lưu được cấu hình email' },
    err_test:            { en: 'Failed to send test email',           vn: 'Không gửi được email thử' },
    err_invalid_email:   { en: 'Please enter a valid email address',  vn: 'Vui lòng nhập địa chỉ email hợp lệ' },
    ok_save:             { en: 'Email configuration saved successfully', vn: 'Đã lưu cấu hình email thành công' },
    ok_test_sent:        { en: 'Test email sent to',       vn: 'Email thử đã gửi đến' },
    val_smtp_host:       { en: 'SMTP Host is required',    vn: 'Vui lòng nhập SMTP Host' },
    val_port:            { en: 'Port is required',         vn: 'Vui lòng nhập cổng' },
    val_from_email:      { en: 'From Email is required',   vn: 'Vui lòng nhập email người gửi' },
    val_email_format:    { en: 'Invalid email format',     vn: 'Định dạng email không hợp lệ' },
    val_from_name:       { en: 'From Name is required',    vn: 'Vui lòng nhập tên người gửi' },
};

interface EmailConfigForm {
    smtpHost: string;
    smtpPort: number;
    fromEmail: string;
    fromName: string;
    cooldownMinutes: number;
    appBaseUrl: string;
}

export default function EmailConfig() {
    const { instance, accounts, inProgress } = useMsal();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testEmail, setTestEmail] = useState('');
    const [testing, setTesting] = useState(false);
    const [role, setRole] = useState<UserRole>('owner');
    const [photoData, setPhotoData] = useState<string | null>(null);
    const [lang] = useLang();
    const L = (key: keyof typeof LABELS) => LABELS[key][lang];

    const { register, handleSubmit, reset, formState: { errors } } = useForm<EmailConfigForm>();

    const auths = () => ({
        headers: {
            'Content-type': 'application/json',
            Authorization: 'Bearer ' + sessionStorage.getItem('accessToken'),
        },
    });

    // Auth + admin check
    useEffect(() => {
        if (inProgress !== InteractionStatus.None) return;
        acquireGetAccessToken(instance, accounts).then(async (tokens: any) => {
            if (!tokens) return;
            // photo
            try {
                const r = await axios('https://graph.microsoft.com/v1.0/me/photo/$value', {
                    headers: { Authorization: `Bearer ${tokens.accessTokenProfile}` },
                    responseType: 'arraybuffer',
                });
                setPhotoData('data:image/jpeg;base64, ' + Buffer.from(r.data, 'binary').toString('base64'));
            } catch { /* no photo */ }
            // admin check
            const userRole = await checkRole(tokens.accessTokenProfile);
            saveRealRole(userRole);
            if (userRole !== 'superadmin') {
                navigate('/accessdenied');
                return;
            }
            setRole(getEffectiveRole(userRole));
        });
    }, [inProgress, instance, accounts, navigate]);

    // Load config
    useEffect(() => {
        if (role !== 'superadmin') return;
        axios.get(`${process.env.REACT_APP_API_END_POINT}/v1/EmailConfig`, auths())
            .then(res => {
                reset({
                    smtpHost: res.data.smtpHost ?? '',
                    smtpPort: res.data.smtpPort ?? 25,
                    fromEmail: res.data.fromEmail ?? '',
                    fromName: res.data.fromName ?? '',
                    cooldownMinutes: res.data.cooldownMinutes ?? 5,
                    appBaseUrl: res.data.appBaseUrl ?? '',
                });
            })
            .catch(() => toast.error(L('err_load')))
            .finally(() => setLoading(false));
    }, [role, reset]);

    const onSendTest = () => {
        if (!testEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmail)) {
            toast.error(L('err_invalid_email'));
            return;
        }
        setTesting(true);
        axios.post(`${process.env.REACT_APP_API_END_POINT}/v1/EmailConfig/test`, { toEmail: testEmail }, auths())
            .then(() => toast.success(L('ok_test_sent') + ' ' + testEmail))
            .catch((err) => {
                const msg = err.response?.data?.message || err.response?.data?.Message || err.response?.data?.detail?.Message || err.message || L('err_test');
                toast.error(msg);
                console.error('[EmailConfig/test]', err.response?.data ?? err);
            })
            .finally(() => setTesting(false));
    };

    const onSubmit = (data: EmailConfigForm) => {
        setSaving(true);
        axios.put(`${process.env.REACT_APP_API_END_POINT}/v1/EmailConfig/update`, data, auths())
            .then(() => toast.success(L('ok_save')))
            .catch(() => toast.error(L('err_save')))
            .finally(() => setSaving(false));
    };

    if (loading) return <LoadingScreen />;

    return (
        <div>
            <Topbar role={role} photoData={photoData ?? undefined} triggerSearch={() => {}} />
            <main className="admin-main" style={{ padding: '36px 40px' }}>
                <div style={{ maxWidth: 720 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
                        <BiEnvelope size={24} color="var(--primary-500)" />
                        <h4 style={{ margin: 0, fontWeight: 700, color: 'var(--n-900)' }}>{L('page_title')}</h4>
                    </div>

                    <div style={{ background: '#fff', borderRadius: 'var(--r-md)', padding: '28px 32px', boxShadow: 'var(--sh-sm)', border: '1px solid var(--n-200)' }}>
                        <p style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--primary-600)', marginBottom: 20 }}>{L('section_smtp')}</p>

                        <form onSubmit={handleSubmit(onSubmit)} noValidate>
                            {/* Row 1 */}
                            <div className="row g-3 mb-3">
                                <div className="col-md-8">
                                    <label className="form-label">{L('smtp_host')}</label>
                                    <input
                                        type="text"
                                        className={`form-control${errors.smtpHost ? ' is-invalid' : ''}`}
                                        placeholder="e.g. smtprelay.global.local"
                                        {...register('smtpHost', { required: L('val_smtp_host') })}
                                    />
                                    {errors.smtpHost && <div className="invalid-feedback">{errors.smtpHost.message}</div>}
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">{L('smtp_port')}</label>
                                    <input
                                        type="number"
                                        className={`form-control${errors.smtpPort ? ' is-invalid' : ''}`}
                                        placeholder="25"
                                        {...register('smtpPort', {
                                            required: L('val_port'),
                                            valueAsNumber: true,
                                            min: { value: 1, message: 'Min 1' },
                                            max: { value: 65535, message: 'Max 65535' },
                                        })}
                                    />
                                    {errors.smtpPort && <div className="invalid-feedback">{errors.smtpPort.message}</div>}
                                </div>
                            </div>

                            {/* Row 2 */}
                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <label className="form-label">{L('from_email')}</label>
                                    <input
                                        type="email"
                                        className={`form-control${errors.fromEmail ? ' is-invalid' : ''}`}
                                        placeholder="e.g. noreply@example.com"
                                        {...register('fromEmail', {
                                            required: L('val_from_email'),
                                            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: L('val_email_format') },
                                        })}
                                    />
                                    {errors.fromEmail && <div className="invalid-feedback">{errors.fromEmail.message}</div>}
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">{L('from_name')}</label>
                                    <input
                                        type="text"
                                        className={`form-control${errors.fromName ? ' is-invalid' : ''}`}
                                        placeholder="e.g. HR Events System"
                                        {...register('fromName', { required: L('val_from_name') })}
                                    />
                                    {errors.fromName && <div className="invalid-feedback">{errors.fromName.message}</div>}
                                </div>
                            </div>

                            {/* Row 3 — Cooldown */}
                            <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                    <label className="form-label">{L('cooldown')}</label>
                                    <div className="input-group">
                                        <input
                                            type="number"
                                            className={`form-control${errors.cooldownMinutes ? ' is-invalid' : ''}`}
                                            {...register('cooldownMinutes', {
                                                required: 'Required',
                                                valueAsNumber: true,
                                                min: { value: 1, message: 'Min 1 minute' },
                                                max: { value: 1440, message: 'Max 1440 minutes (24h)' },
                                            })}
                                        />
                                        <span className="input-group-text" style={{ color: 'var(--n-500)', fontSize: '0.85rem' }}>min</span>
                                        {errors.cooldownMinutes && <div className="invalid-feedback">{errors.cooldownMinutes.message}</div>}
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--n-400)', marginTop: 4 }}>
                                        {L('hint_cooldown')}
                                    </div>
                                </div>
                            </div>

                            {/* Row 4 — App Base URL */}
                            <div className="row g-3 mb-4">
                                <div className="col-12">
                                    <label className="form-label">{L('app_base_url')}</label>
                                    <input
                                        type="url"
                                        className={`form-control${errors.appBaseUrl ? ' is-invalid' : ''}`}
                                        placeholder="e.g. https://hr-events.example.com"
                                        {...register('appBaseUrl')}
                                    />
                                    {errors.appBaseUrl && <div className="invalid-feedback">{errors.appBaseUrl.message}</div>}
                                    <div style={{ fontSize: '0.75rem', color: 'var(--n-400)', marginTop: 4 }}>
                                        {L('hint_app_base_url')}
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: 10 }}>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    style={{
                                        minWidth: 120,
                                        padding: '8px 24px',
                                        background: 'linear-gradient(135deg, var(--primary-500) 0%, var(--primary-700) 100%)',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: 'var(--r-sm)',
                                        fontWeight: 600,
                                        fontSize: '0.9rem',
                                        cursor: saving ? 'not-allowed' : 'pointer',
                                        opacity: saving ? 0.7 : 1,
                                    }}
                                >
                                    {saving ? (
                                        <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />{L('btn_saving')}</>
                                    ) : L('btn_save')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => reset()}
                                    style={{
                                        padding: '8px 20px',
                                        background: 'transparent',
                                        color: 'var(--n-500)',
                                        border: '1px solid var(--n-300)',
                                        borderRadius: 'var(--r-sm)',
                                        fontWeight: 500,
                                        fontSize: '0.9rem',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {L('btn_reset')}
                                </button>
                            </div>
                        </form>
                    </div>
                    {/* Test Email Card */}
                    <div style={{ background: '#fff', borderRadius: 'var(--r-md)', padding: '28px 32px', boxShadow: 'var(--sh-sm)', border: '1px solid var(--n-200)', marginTop: 20 }}>
                        <p style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--primary-600)', marginBottom: 20 }}>{L('section_test')}</p>
                        <div className="row g-3 align-items-end">
                            <div className="col-md-8">
                                <label className="form-label">{L('recipient_email')}</label>
                                <input
                                    type="email"
                                    className="form-control"
                                    placeholder="e.g. you@example.com"
                                    value={testEmail}
                                    onChange={e => setTestEmail(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && onSendTest()}
                                />
                            </div>
                            <div className="col-md-4">
                                <button
                                    type="button"
                                    onClick={onSendTest}
                                    disabled={testing}
                                    style={{
                                        width: '100%',
                                        padding: '8px 16px',
                                        background: testing ? 'var(--n-200)' : 'linear-gradient(135deg, var(--primary-500) 0%, var(--primary-700) 100%)',
                                        color: testing ? 'var(--n-500)' : '#fff',
                                        border: 'none',
                                        borderRadius: 'var(--r-sm)',
                                        fontWeight: 600,
                                        fontSize: '0.9rem',
                                        cursor: testing ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 6,
                                    }}
                                >
                                    {testing ? (
                                       <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />{L('btn_sending')}</>
                                    ) : (
                                       <><BiSend size={16} />{L('btn_send_test')}</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
            <ToastContainer position="top-right" autoClose={3000} />
        </div>
    );
}
